import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import * as Yup from "yup";
import { modifyStackGarment } from "../functions";
import { showMessage } from "store/modules/Message";
import { useSelector, useDispatch } from "react-redux";
import { setDefaultData } from "store/modules/StackGarment";

const validationSchema = Yup.object({
  tallas: Yup.array().of(
    Yup.object().shape({
      cantidad: Yup.number()
        .typeError("Debe ser un número")
        .required("Campo obligatorio")
        .min(0, "Debe ser mayor o igual a 0"),
      descripcion: Yup.string().max(100, "Máximo 100 caracteres"),
    })
  ),
});

const StackGarmentForm = ({
  mode,
  handleClose,
  setRender,
  productoData,
  setFieldValue,
}) => {
  const { defaultData } = useSelector(({ stackGarment }) => stackGarment);
  const dispatch = useDispatch();

  const messages = (mensaje) => {
    dispatch(
      showMessage({
        message: `El inventario de ropa se ha ${mensaje} con éxito`,
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      })
    );
  };
  const formik = useFormik({
    initialValues: {
      tallas:
        defaultData?.tallas?.map((t) => ({
          id: t.id,
          idTalla: t.idTalla,
          idProducto: t.idProducto,
          nombre: t.nombre,
          cantidad: t.cantidad || "",
          descripcion: t.descripcion || "",
        })) || [],
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setRender(true);
        await Promise.all(
          values.tallas.map((item) => {
            const data = {
              id: item.id,
              producto: { id: item.idProducto },
              talla: { id: item.idTalla },
              cantidad: item.cantidad,
              descripcion: item.descripcion,
            };
            return modifyStackGarment(data);
          })
        );
        messages("editado");
        dispatch(setDefaultData({}));
        handleClose();
        setRender(false);
      } catch (error) {
        console.error("Error al actualizar:", error);
      }
    },
  });

  useEffect(() => {
    if (mode === "POST" && defaultData?.tallas) {
      formik.setValues({
        tallas: defaultData.tallas.map((t) => ({
          id: t.id,
          idTalla: t.idTalla,
          idProducto: t.idProducto,
          nombre: t.nombre,
          cantidad: "",
          descripcion: "",
        })),
      });
    }
  }, [defaultData, mode]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            sx={{ textAlign: "center", fontWeight: "bold" }}
          >
            {"Modelo: " + (defaultData?.nombre || "")}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            sx={{ textAlign: "center", fontWeight: "bold" }}
          >
            {"SKU: " + (defaultData?.sku || "")}
          </Typography>
        </Grid>

        {/* 🔽 NUEVA SECCIÓN DE IMAGEN */}
        <Grid item xs={12}>
          {defaultData?.imagen ? (
            <Box
              component="img"
              src={defaultData.imagen}
              alt="Imagen del modelo"
              sx={{
                width: "250px",
                height: "auto",
                borderRadius: 2,
                boxShadow: 3,
                mt: 2,
                mx: "auto", // 🔥 centra horizontalmente
                display: "block", // 🔥 necesario para que mx:auto funcione
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, textAlign: "center" }}
            >
              No hay imagen disponible para este modelo.
            </Typography>
          )}
        </Grid>

        {mode === "POST" && (
          <Grid item xs={12}>
            <Autocomplete
              id="model"
              name="model"
              fullWidth
              options={productoData || []}
              renderInput={(params) => (
                <TextField {...params} label="Modelo" color="primary" />
              )}
              getOptionLabel={(option) => `${option.nombre} ( ${option.sku} )`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => {
                if (value) {
                  setFieldValue(value.id);
                }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {`${option.nombre} ( ${option.sku} )`}
                </li>
              )}
            />
          </Grid>
        )}

        {/* 🔽 Campos de tallas */}
        <Grid item xs={12}>
          {formik.values.tallas.length > 0 ? (
            <>
              <Typography
                textAlign="center"
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Especifique la cantidad por talla
              </Typography>

              {formik.values.tallas.map((item, index) => (
                <div key={index} className="flex justify-center">
                  <div className="grid grid-cols-3 gap-4 p-1 px-2">
                    {/* COLUMNA TALLA */}
                    <div className="flex w-80 flex-col items-start justify-center rounded-2xl bg-white px-3 py-4 shadow-md">
                      <p className="text-sm text-gray-600">Talla:</p>
                      <p className="text-base font-medium text-navy-700 dark:text-white">
                        {item.nombre}
                      </p>
                    </div>

                    {/* COLUMNA CANTIDAD */}
                    <div className="flex w-80 flex-col justify-center rounded-2xl bg-white px-3 py-4 shadow-md">
                      <TextField
                        label="Cantidad"
                        name={`tallas[${index}].cantidad`}
                        value={item.cantidad}
                        onChange={formik.handleChange}
                        type="number"
                        fullWidth
                        error={
                          formik.touched.tallas?.[index]?.cantidad &&
                          Boolean(formik.errors.tallas?.[index]?.cantidad)
                        }
                        helperText={
                          formik.touched.tallas?.[index]?.cantidad &&
                          formik.errors.tallas?.[index]?.cantidad
                        }
                      />
                    </div>

                    {/* COLUMNA DESCRIPCIÓN */}
                    <div className="flex w-80 flex-col justify-center rounded-2xl bg-white px-3 py-4 shadow-md">
                      <TextField
                        label="Descripción"
                        name={`tallas[${index}].descripcion`}
                        value={item.descripcion}
                        onChange={formik.handleChange}
                        type="text"
                        fullWidth
                        error={
                          formik.touched.tallas?.[index]?.descripcion &&
                          Boolean(formik.errors.tallas?.[index]?.descripcion)
                        }
                        helperText={
                          formik.touched.tallas?.[index]?.descripcion &&
                          formik.errors.tallas?.[index]?.descripcion
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <Typography textAlign="center" color="text.secondary">
              No hay tallas disponibles para este producto.
            </Typography>
          )}
        </Grid>

        {/* BOTÓN DE ENVÍO */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end", // alinea a la derecha
              gap: 2, // espacio entre los botones
              position: "sticky", // los mantiene visibles al hacer scroll si quieres
              bottom: 0,
              padding: 2,
              zIndex: 1,
            }}
          >
            <Button variant="outlined" color="primary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={defaultData.tallas === undefined}
            >
              Guardar
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};

export default StackGarmentForm;
